export type CustomerGeography = "AUS" | "DE" | "ES" | "IE" | "NL" | "UK" | "US";

interface CustomerGridLogo {
  src: string;
  width: number;
  height: number;
  /** "light" = light-coloured glyphs; "matte" = opaque white background. */
  treatment?: "light" | "matte";
  displaySize?: "detailed";
}

export interface CustomerShortCard {
  stat: string;
  label?: string;
  /** Sanity CDN background image shown behind the expanded card copy. */
  bgSrc?: string;
}

interface CustomerQuote {
  en: string;
  de?: string;
}

export interface CustomerGridBrand {
  id: string;
  name: string;
  geography: CustomerGeography;
  logo?: CustomerGridLogo;
  video?: {
    assetId: string;
    playbackId: string;
    /** Seconds into the video to grab the poster frame from (default 1). */
    posterTime?: number;
  };
  href: string;
  shortCard?: CustomerShortCard;
  quote?: CustomerQuote;
}

export interface LocalizedCustomerGridBrand extends Omit<
  CustomerGridBrand,
  "quote"
> {
  quote?: string;
  /**
   * False when `quote` fell back to English because the brand has no
   * translation for the requested language — callers should prefer localised
   * copy over showing an English paragraph.
   */
  quoteIsLocalized: boolean;
}

const SANITY = "https://cdn.sanity.io/images/1aendizt/redesign";

function logo(
  path: string,
  width: number,
  height: number,
  treatment?: "light" | "matte",
  displaySize?: "detailed"
): CustomerGridLogo {
  return {
    src: `${SANITY}/${path}`,
    width,
    height,
    treatment,
    displaySize,
  };
}

function bg(path: string): string {
  return `${SANITY}/${path}`;
}

function fileLogo(
  path: string,
  width: number,
  height: number
): CustomerGridLogo {
  return {
    src: `https://cdn.sanity.io/files/1aendizt/redesign/${path}`,
    width,
    height,
  };
}

export const CUSTOMER_GRID_BRANDS: readonly CustomerGridBrand[] = [
  {
    id: "butchers-dog",
    name: "butchers dog",
    geography: "AUS",
    logo: logo(
      "2a10d8ba0b2b0e76318d4b29c5c69d1480d6206c-425x270.webp",
      425,
      270,
      "light",
      "detailed"
    ),
    href: "/our-customers",
  },
  {
    id: "3bears",
    name: "3Bears",
    geography: "DE",
    logo: logo(
      "aafb305d0b1878edef8a4a5c9734ffec3d97c53e-155x118.png",
      155,
      118
    ),
    href: "/our-customers/3bears",
    shortCard: {
      stat: "2,000+",
      label: "New points of sale",
      bgSrc: bg("1c4951f258b7687cfe2e66bfbaacb87481865573-498x371.png"),
    },
    quote: {
      en: "It's good to have a partner like Wayflyer who understands how a business works, what it needs, believes in our vision and wants to go this way with us.",
      de: "Wayflyer als Plattform wurde in der Gründerszene wegen ihrer hohen Flexibilität und ihres sehr transparenten Prozesses mehrfach erwähnt.",
    },
  },
  {
    id: "maniko-nails",
    name: "maniko-nails",
    geography: "DE",
    logo: logo(
      "917c8e0232666ed78e2817822a0a048e00acf993-1200x628.webp?rect=0,190,1200,248",
      1200,
      248
    ),
    href: "/our-customers",
  },
  {
    id: "tallow-naturals",
    name: "Tallow Naturals",
    geography: "DE",
    logo: logo(
      "dbd0829acb7b9c14107ec04cd50121e41fa8f961-913x278.png",
      913,
      278
    ),
    href: "/our-customers",
    quote: {
      en: "Working with Wayflyer was fast and completely uncomplicated, from application to funding took only a few days, with none of the friction we're used to from traditional lenders. As a fast-growing eCommerce business, that kind of speed and flexibility isn't a nice-to-have, it's exactly what we need to keep scaling without losing momentum.",
    },
  },
  {
    id: "valkental",
    name: "Valkental",
    geography: "DE",
    logo: logo(
      "eb3d10226f518ef442f69ca653b727c3a001c856-1244x312.png",
      1244,
      312
    ),
    href: "/our-customers/valkental",
    quote: {
      en: "In the end, your offer was the best fit for our needs. It was simple, easy to understand, and communication with your colleagues was smooth... You helped us finance a very large production run, which contributed to a very successful year. That was a huge help.",
      de: "Wayflyer hat uns geholfen, eine sehr große Produktion zu finanzieren, was zu einem sehr erfolgreichen Jahr beigetragen hat. Das war eine riesige Hilfe.",
    },
  },
  {
    id: "laagam",
    name: "Laagam",
    geography: "ES",
    logo: logo(
      "4ed81ec4247b41ae72d0d3a892f8068a72af300c-1200x630.webp?rect=120,250,960,130",
      960,
      130
    ),
    href: "/our-customers",
    quote: {
      en: "Wayflyer combines fast decisions, a strong understanding of our industry, and great ongoing support. We would highly recommend them.",
    },
  },
  {
    id: "pestle-and-mortar",
    name: "Pestle & Mortar",
    geography: "IE",
    logo: logo(
      "c62c174b53c58759b5af5c20bf8cb84d83c486ef-1000x576.svg",
      1000,
      576
    ),
    href: "/our-customers",
  },
  {
    id: "hears",
    name: "Hears",
    geography: "NL",
    logo: logo(
      "f4654e08c224e153bff8143e733c910f2560c14f-1890x413.webp",
      1890,
      413,
      "light"
    ),
    href: "/our-customers",
  },
  {
    id: "nutri-bites",
    name: "Nutri Bites",
    geography: "NL",
    logo: logo(
      "0681482777461ba6372566b121e584f9e9b3bfc4-1024x640.webp?rect=64,136,896,368",
      896,
      368
    ),
    href: "/our-customers",
    quote: {
      en: "Working with Wayflyer feels like having additional financial strength in our corner. We know that when a new opportunity or capital requirement arises, we can reach out, discuss what the business needs and explore the next step together. The communication is direct, transparent and refreshingly straightforward.",
    },
  },
  {
    id: "cole-buxton",
    name: "Cole Buxton",
    geography: "UK",
    logo: logo(
      "c3ad7511d1c17028ea906bcf62925d1cba2dbae4-1200x628.png?rect=0,228,1200,174",
      1200,
      174
    ),
    href: "/our-customers/cole-buxton",
    shortCard: {
      stat: "100%",
      label: "Founder owned, because they never needed to give away equity",
      bgSrc: bg("92a4ebd4b3f6b021ca297b134e7bd33a8cc7d678-1280x720.jpg"),
    },
    quote: {
      en: "We've scaled it up to a great place but still privately owning the company 100 percent. It would have been not impossible but it definitely wouldn't have been easy to get to that without Wayflyer really over the last couple of years.",
    },
  },
  {
    id: "correxiko",
    name: "Correxiko",
    geography: "UK",
    logo: logo(
      "c63cd56b7e7141fd62f38a9f2134a671aad49088-300x300.png?rect=24,132,252,46",
      252,
      46
    ),
    video: {
      assetId: "PTOtXiuEIfXj002MWLfZeWrqamZ6cJERx4fFutY2Weik",
      playbackId: "MUarqz28avx01ekI01QVHTP02UEMCCF01wyDPo018aQ8kW00w",
    },
    href: "/our-customers/correxiko",
    shortCard: {
      stat: "3-4x",
      label: "Bigger offer received than any other lender",
      bgSrc: bg("756b37f0e77472ebd2a6e90dd50eb6b93e430067-1828x1024.webp"),
    },
    quote: {
      en: "What I really loved about them was they contacted us and I spoke to their finance team and they really understood what our business was... Wayflyer really took the time to understand us and by doing that they gave us three or four times what everyone else offered us and they offered it fast.",
    },
  },
  {
    id: "drowsy-sleepco",
    name: "drowsy sleepco",
    geography: "UK",
    logo: logo(
      "1bdae0654b8b2cda86adccbbd752a39cde9f33e3-2881x442.webp",
      2881,
      442
    ),
    href: "/our-customers",
  },
  {
    id: "frive",
    name: "Frive",
    geography: "UK",
    logo: logo(
      "10219194860f45d79f32767f16330e665ff92f70-250x128.png",
      250,
      128
    ),
    href: "/our-customers",
    shortCard: {
      stat: "£50M+",
      label: "Revenue, still founder-led",
      bgSrc: bg("8695ea795b4dad29abf6ba85eed4b63e5ececa0e-758x568.png"),
    },
    quote: {
      en: "Wayflyer has been a trusted partner to Frive for several years, providing flexible funding that has supported our growth while making the process straightforward and efficient.",
    },
  },
  {
    id: "get-swim-go",
    name: "Get Swim Go",
    geography: "UK",
    logo: logo("a3666ed3c5d27045f80d0b556b7e853bf22dec70-509x87.webp", 509, 87),
    href: "/our-customers",
    quote: {
      en: "Wayflyer again stepped in there with a short term three month facility just so we could continue or we could kick off the builds on the two new sites in line with the timing plan... short term fulfilled quickly and profitable businesses move on.",
    },
  },
  {
    id: "heights",
    name: "heights",
    geography: "UK",
    logo: logo(
      "9fde9aac3035a750a3a1fa972d8a66bca9316f83-213x185.png?rect=24,44,166,96",
      166,
      96
    ),
    href: "/our-customers",
    quote: {
      en: "Wayflyer were there for us when we needed it and always reliable and helpful in our pursuit of growth.",
    },
  },
  {
    id: "lick",
    name: "lick",
    geography: "UK",
    logo: logo(
      "c24ece21f169da39ab1e6c7b5b9e2547a585789e-768x274.png",
      768,
      274
    ),
    href: "/our-customers/lick",
    quote: {
      en: "It's been great to work with Wayflyer to enable us to have capital for different short term cash requirements, whether it be inventory building, or managing cash flow.",
    },
  },
  {
    id: "myomaster",
    name: "MyoMaster",
    geography: "UK",
    logo: logo(
      "b269d89c669751698da0e53ddd57be6ea888d5f6-501x242.webp",
      501,
      242
    ),
    href: "/our-customers/myomaster",
    shortCard: {
      stat: "200+",
      label: "Investors said no, Wayflyer funded the potential",
      bgSrc: bg("3df0488a3ed68e9cdb6f9f7d21738723fb209615-400x1020.webp"),
    },
    quote: {
      en: "We started working with Wayflyer in 2021 they have completely supported our growth probably like nobody else and now we've got kind of banks and investors you know calling all over us trying to give us cash and Wayflyer is still the relationship that we hold on to and that we choose.",
    },
  },
  {
    id: "perfectted",
    name: "perfectted",
    geography: "UK",
    logo: logo(
      "250d6ff0c57265bbc34c0c7291f8323e8a14c8cf-1921x1010.png",
      1921,
      1010
    ),
    href: "/our-customers/perfectted",
    shortCard: {
      stat: "400+",
      label: "Stores at launch",
      bgSrc: bg("5c255b42ebf62d2390de11c85fac5e25ab17c55e-1200x1200.webp"),
    },
    quote: {
      en: "We've been able to have a conversation with Wayflyer and said 'Guys we need more money not because we're struggling we need more money because we've got massive opportunities here.' So we would be in deep trouble without them genuinely.",
    },
  },
  {
    id: "shuffle-club",
    name: "Shuffle Club",
    geography: "UK",
    logo: logo(
      "8aa506e28c5c648dd1d8770e12fd06cb5a02a56f-1200x431.png",
      1200,
      431
    ),
    href: "/our-customers",
    quote: {
      en: "The Wayflyer funding there is just really to bridge the seasonality of the business in the short term... Wayflyer will have provided exactly what we needed when we needed it to keep a profitable business moving forward.",
    },
  },
  {
    id: "sons",
    name: "Sons UK & IRE",
    geography: "UK",
    logo: logo(
      "4302a8008ff14517fece06200ab12b072a7bc925-404x125.png",
      404,
      125,
      "matte"
    ),
    href: "/our-customers/sons",
    shortCard: {
      stat: "300%+",
      label: "Revenue growth with Wayflyer",
      bgSrc: bg("e45fd67a03a9feb35c5ec7b7820680e90735ac77-1200x1500.webp"),
    },
    quote: {
      en: "We spoke to pretty much every funding provider out there. Ultimately, we chose to work with Wayflyer because of how impressed we were with the team and the fact that their product offers support beyond just funding, such as marketing insights which was really valuable in the early stages.",
    },
  },
  {
    id: "suri",
    name: "Suri",
    geography: "UK",
    logo: fileLogo("6dfa5e43f7ff300664c81a0d41d1101011c880b5.svg", 111, 32),
    href: "/our-customers",
    quote: {
      en: "We are all about responsible profitable lean growth which is probably why Wayflyer keep loaning us money because it just works right you know you can see how if you have a great business model that's profitable and growing that actually working capital is a requirement.",
    },
  },
  {
    id: "wednesdays-domain",
    name: "Wednesdays Domain",
    geography: "UK",
    logo: logo(
      "4e540399dc08ae75fe6c0ff27adfaa03c552966c-1270x1111.webp?rect=96,80,1088,920",
      1088,
      920,
      undefined,
      "detailed"
    ),
    href: "/our-customers",
    quote: {
      en: "We finance the business through a mix of equity and debt... any debt that we're using, Wayflyer being the provider here, that goes into purchasing stock... effectively we've got business cash coming into the business 50 days before we have to pay for it.",
    },
  },
  {
    id: "act-and-acre",
    name: "Act+Acre",
    geography: "US",
    logo: logo(
      "3a5c9d35a6d068c8f056d19d62a0a7d2307ad7e3-1410x1231.svg",
      1410,
      1231
    ),
    href: "/our-customers/actandacre",
    shortCard: {
      stat: "235",
      label: "Sephora stores nationwide",
      // rect crop drops the baked-in "Act+Acre" wordmark at bottom-left
      bgSrc: bg(
        "7a0561f23f71e9144c0029b22f03d740a48ebe39-2112x1408.png?rect=480,0,1632,1408"
      ),
    },
    quote: {
      en: "Wayflyer isn't just a finance provider they're a strategic growth partner for founders looking to scale quickly while maintaining ownership. Wayflyer is a gamechanger.",
    },
  },
  {
    id: "brez",
    name: "Brez",
    geography: "US",
    logo: logo(
      "6bbfb7b752f3f3e2048106946ff2f9b7ec9760de-1966x954.png",
      1966,
      954
    ),
    href: "/our-customers/brez",
    shortCard: {
      stat: "$44M",
      label: "Revenue in two years",
      bgSrc: bg("c8fc64e39057d43e1d3c6f29b5ded36ab52aedf8-1707x1058.jpg"),
    },
  },
  {
    id: "cosy-house-collection",
    name: "Cosy House Collection",
    geography: "US",
    logo: logo(
      "dbf12936bf074d589e51fca07afd15db0035aa23-600x180.jpg?rect=0,18,590,144",
      590,
      144
    ),
    href: "/our-customers",
  },
  {
    id: "freda-salvador",
    name: "Freda Salvador",
    geography: "US",
    logo: logo(
      "82ccfcaa8b77d08052ed81c7dac60bd791323074-750x197.webp",
      750,
      197
    ),
    href: "/our-customers",
  },
  {
    id: "ghost-golf",
    name: "Ghost Golf",
    geography: "US",
    logo: logo(
      "25fb221a3692750e802e894e0edfcfed57e45aa3-1320x156.png",
      1320,
      156,
      "light"
    ),
    video: {
      assetId: "1iQUOnSP7iXDTkkFVPXNXzWB8lczV2aKiOkmHCjCu3Q",
      playbackId: "T8i00KOfaGuH3Zt37FcKDOEYb702U5uDsV02RDg013vKi5M",
    },
    href: "/our-customers/ghost-golf",
    shortCard: {
      stat: "$2M",
      label: "In unmet demand before Wayflyer",
      bgSrc: bg("3928c35f041916c915a5a135a879eb85e98b0e5e-1820x1028.webp"),
    },
    quote: {
      en: "When we came in contact with Wayflyer the process at that time was very simple they understood our marketing strategy they understood our need for inventory... my biggest regret last year was not having that conversation with Wayflyer sooner.",
    },
  },
  {
    id: "kekoa-foods",
    name: "Kekoa Foods",
    geography: "US",
    logo: logo(
      "1b64a5cd7c8f67b87118a659811fe35abd47b66f-89x48.svg",
      89,
      48,
      "light"
    ),
    video: {
      assetId: "RI01lGIfZ4aGtfq9r73RnynjSkUSEBFwPPf004Uopp4hE",
      playbackId: "h4fc6OrDLmRIAlaLkbaF402imWJbOoGZ00aTi9CGxRfNE",
    },
    href: "/our-customers/kekoa-foods",
    shortCard: {
      stat: "1 → 800",
      label: "Stores in one year",
      bgSrc: bg("05e79f7c35a137581b9db0b93490d2c441880515-2512x1466.png"),
    },
    quote: {
      en: "Wayflyer was really able to help bridge that financial gap from initial pouch creation to being on the shelf and receiving the money so that we could keep things going... everyone was super personable, they really wanted to make sure that our brand succeeded.",
    },
  },
  {
    id: "little-words-project",
    name: "Little Words Project",
    geography: "US",
    logo: logo("6a3e34619c318aa4a543032dc27bae23ad13143b-340x73.png", 340, 73),
    video: {
      assetId: "kQcbPHh6JSfgXzB02xhVwLlzhwNMpB1gCw02sXvBCcdoo",
      playbackId: "ALOj7jKC8VSWjW9JB00Q7lgvCvqKZyx9CGLECofdWYOc",
      // Burnt-in subtitles run from ~5s to ~27s; 0.6s is the bracelet detail.
      posterTime: 0.6,
    },
    href: "/our-customers/little-words-project",
    shortCard: {
      stat: "15 → 100",
      label: "Retail store expansion underway",
      bgSrc: bg("b4b354233611db678904386404ec369f99fa274e-1440x941.jpg"),
    },
    quote: {
      en: "It's what made Wayflyer such a hero because they just have such a profound understanding of the DTC landscape and they really took the time to understand our business and our business's needs so they just knew it wasn't a risk and were happy to support us.",
    },
  },
  {
    id: "nana-joes-granola",
    name: "Nana Joes Granola",
    geography: "US",
    logo: logo(
      "fb51aeef556d00a897f62508b85ad1e3bb55cb24-1200x628.webp?rect=152,88,916,452",
      916,
      452
    ),
    href: "/our-customers",
    shortCard: {
      stat: "15%",
      label: "Margin increase",
      bgSrc: bg("8a5bc34953aa369386a42388f6a6d0a3d8358b61-750x750.jpg"),
    },
    quote: {
      en: "The experience was seamless. It was very easy. We applied and then we submitted all of our materials and got the money pretty quickly... I think the team really wanted to get to know me. And they really wanted to get to know what I needed that money for and my business model.",
    },
  },
  {
    id: "sand-cloud",
    name: "Sand Cloud",
    geography: "US",
    logo: logo("de296e177731210bcdae6dfa27ad2cb250fbe8b4-150x32.svg", 150, 32),
    video: {
      assetId: "eKtZhYTMI8TSmadIdw8Cu3BnhfbVboox4csH00ndITTY",
      playbackId: "rkBLa027302bTY02kbiVQuKCnzc98gBTrNgK5oCqrfLN6I",
    },
    href: "/our-customers/sand-cloud",
    shortCard: {
      stat: "$16.1M",
      label: "Annual revenue, zero equity given up.",
      bgSrc: bg("02b284bac6aea3c585be9c03e48ab65573d7cd1e-1920x1080.webp"),
    },
    quote: {
      en: "One of the things that I love about Wayflyer is it doesn't feel like a big banking relationship it's personal they understand eCommerce and how we want to spend the money.",
    },
  },
  {
    id: "sierra-madre-golf",
    name: "Sierra Madre Golf",
    geography: "US",
    logo: logo(
      "1b907aa1704b85b95fbaae541178323944423dbf-340x131.avif?rect=0,37,340,52",
      340,
      52,
      "light"
    ),
    href: "/our-customers/sierra-madre-golf",
    shortCard: {
      stat: "150%",
      label: "Year-on-year growth",
      bgSrc: bg("1d9afaba70e941e87d514b5c1ab08c5b6b523118-1456x968.jpg"),
    },
    quote: {
      en: "Wayflyer leveled us up and so that we could make a certain amount of sales so that now we're actually scaling as a business... I don't know how that would have been possible without Wayflyer capital.",
    },
  },
  {
    id: "spongelle",
    name: "Spongellé",
    geography: "US",
    logo: logo("51a91e59522ac1ef65af3f1418d04011fb1e779f-138x32.svg", 138, 32),
    video: {
      assetId: "U00AMNOaTZB6OjLBu00WWw8YGnUOXPa2Z900Q5ma9Yk2O4",
      playbackId: "4ar2sJHoZCmk4o7XZ7D01wil01Ec9024iZr8ReCkddSTUQ",
    },
    href: "/our-customers/spongelle",
    shortCard: {
      stat: "24 hours",
      label: "From application to money in the bank.",
      bgSrc: bg("0ffe6da326c8e9de3db95dbf7c41624b4083d91f-2734x1534.webp"),
    },
    quote: {
      en: "What attracted us to Wayflyer was the ease of use, the efficiency of the partnership and the speed at which we received funding... working with Wayflyer kind of gives us the peace of mind to know that we can take on these new challenges.",
    },
  },
  {
    id: "ta3",
    name: "TA3",
    geography: "US",
    logo: logo("4b171a6640eb69a4086881b0c3c007af9690d7f2-240x66.webp", 240, 66),
    href: "/our-customers/ta3",
    quote: {
      en: "Before we started working with Wayflyer we had a venture debt deal and it was very stressful the second we switched over to Wayflyer it was like everything was very easy it's really helped us with inventory... we couldn't have done it without them.",
    },
  },
  {
    id: "true-classic",
    name: "True Classic",
    geography: "US",
    logo: logo("96522e2bf2ad86034b321a7be0e3c01bb8f35259-150x32.svg", 150, 32),
    video: {
      assetId: "G7XK9vYICWRenPbv69GoR013cBOmI00DmYNZ1Nqtys4yI",
      playbackId: "lf02lIcTXSHWjBF4Z7Lk8VDVvGx35bf2qGwSKyZLkBdY",
    },
    href: "/our-customers/true-classic",
    shortCard: {
      stat: "$20M",
      label: "Additional revenue tied to Wayflyer",
      bgSrc: bg("1403998e1102fa811c271926d430eefb8aea2949-1820x1024.webp"),
    },
    quote: {
      en: "Banks look at your history and want to see proven results whereas Wayflyer can underwrite some of your future growth and revenue in order to finance your operations.",
    },
  },
];

const LOCALE_GEOGRAPHY: Record<string, CustomerGeography> = {
  de: "DE",
  es: "ES",
  nl: "NL",
  "en-AU": "AUS",
  en: "US",
};

const UK_IRELAND_GEOGRAPHIES = [
  "UK",
  "IE",
] as const satisfies readonly CustomerGeography[];

export function getLocaleGeographies(
  locale: string
): readonly CustomerGeography[] {
  if (locale === "en-IE" || locale === "en-GB") {
    return UK_IRELAND_GEOGRAPHIES;
  }

  return [LOCALE_GEOGRAPHY[locale] ?? "US"];
}

function matchesLocaleGeography(
  brandGeography: CustomerGeography,
  locale: string
): boolean {
  return getLocaleGeographies(locale).includes(brandGeography);
}

export function getCustomerGridBrandsForLocale(
  locale: string
): LocalizedCustomerGridBrand[] {
  const localizedLanguage = locale.split("-")[0];

  return CUSTOMER_GRID_BRANDS.toSorted(
    (a, b) =>
      Number(matchesLocaleGeography(b.geography, locale)) -
      Number(matchesLocaleGeography(a.geography, locale))
  ).map(({ quote, ...brand }) => {
    const localized =
      localizedLanguage === "en"
        ? quote?.en
        : localizedLanguage === "de"
          ? quote?.de
          : undefined;

    return {
      ...brand,
      quote: localized ?? quote?.en,
      quoteIsLocalized: Boolean(localized),
    };
  });
}

/**
 * Case-study href for brands with a live story page. Brands without one link
 * to the `/our-customers` listing, which isn't worth a per-tile link.
 */
export function getCustomerCaseStudyHref(
  brand: Pick<CustomerGridBrand, "href">
): string | null {
  return brand.href.startsWith("/our-customers/") ? brand.href : null;
}
